import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Button, Card } from '@betareader/design-system';
import { Media } from 'src/utils/Media';
import { withTranslation } from 'react-i18next';
import qs from 'qs';
import { push } from '../../../navigation';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { uxAnalyticsUtil, authUtil } from '../../../utils';

import ProductCard from './productCard';
import CancelSubscriptionModal from './cancelSubscriptionModal';
import ChangeSubscriptionModal from './changeSubscriptionModal';
import PaymentSuccessModal from './paymentSuccessModal';
import Footer from '../../home/footer';
import ProductToSMessage from '../../../components/payments/productToSMessage';
import { withHostAppProxy } from '../../../contexts/HostAppProxyContext';
import './products.css';

import { getUserProfile } from '../../../modules/user';
import { fetchProducts } from '../../../modules/subscription';

const appleProductIdForPlan = internalId =>
  ({
    standard_monthly: 'io.betareader.premium.monthly',
    standard_yearly: 'io.betareader.premium.yearly',
    pro_monthly: 'io.betareader.pro.monthly',
    pro_yearly: 'io.betareader.pro.yearly',
  })[internalId];

export class Products extends Component {
  constructor(props) {
    super(props);
    this.state = {
      planIntervalFilter: 'monthly',
      nativePurchaseProductId: null,
      paddlePurchasePlanId: null,
    };
    this.yearlyButton = null;
    this.nativeRefreshTimers = [];
  }

  componentDidMount = () => {
    uxAnalyticsUtil.trackPageView(this.props.match.path);
    window.addEventListener('resize', this.updateDimensions);
    const queryObject = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    });
    if (queryObject.coupon !== undefined) {
      this.setState({
        coupon: queryObject.coupon,
      });
    }
    this.fetchProducts();
  };

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.updateDimensions);
    this.nativeRefreshTimers.forEach(timer => clearTimeout(timer));
  };

  componentDidUpdate = prevProps => {
    const { userProfile, products, t } = this.props;
    const { planIntervalFilter } = this.state;
    if (prevProps.userProfile && userProfile) {
      const previousPricingPlanId =
        prevProps.userProfile.account.subscription.pricingPlan.externalId;
      const currentPricingPlanId =
        userProfile.account.subscription.pricingPlan.externalId;
      // check if we moved plan
      if (previousPricingPlanId !== currentPricingPlanId) {
        toast.info(t('SubscriptionChangeSuccess'));
      }
    }
    const previousNativeEvent =
      prevProps.hostAppProxy?.settings?.nativePurchaseEvent;
    const nativeEvent = this.props.hostAppProxy?.settings?.nativePurchaseEvent;
    if (nativeEvent && nativeEvent !== previousNativeEvent) {
      this.setState({ nativePurchaseProductId: null });
      const status = nativeEvent.data?.status;
      if (status === 'success') {
        toast.success(
          nativeEvent.data?.operation === 'restore'
            ? t('PurchasesRestored', 'Purchases restored.')
            : t('PurchaseSuccessful', 'Purchase successful.')
        );
        [500, 2000, 5000, 10000].forEach(delay => {
          this.nativeRefreshTimers.push(
            setTimeout(async () => {
              const idToken = await authUtil.getFreshIdToken();
              this.props.getUserProfile(idToken);
            }, delay)
          );
        });
      } else if (status === 'cancelled') {
        toast.info(t('Cancelled'));
      } else if (status === 'error') {
        toast.error(
          t('PurchaseFailed', 'The purchase could not be completed.')
        );
      }
    }
    // select the active interval button
    if (userProfile && !planIntervalFilter) {
      const currentPlanInterval =
        userProfile.account.subscription.pricingPlan.billingInterval;
      // if current plan is free, set the default active button to monthly
      if (currentPlanInterval === 'none' || currentPlanInterval === 'free') {
        this.setState({ planIntervalFilter: 'monthly' });
      } else {
        // If we have a montly or yearly plan set it to planIntervalFilter
        this.setState({ planIntervalFilter: currentPlanInterval });
      }
    }
  };

  fetchProducts = async () => {
    const idToken = await authUtil.getFreshIdToken();
    const { userProfile } = this.props;
    this.props.fetchProducts(idToken);
    // select the active interval button
    if (userProfile) {
      const currentPlanInterval =
        userProfile.account.subscription.pricingPlan.billingInterval;
      // if current plan is free, set the default active button to monthly
      if (currentPlanInterval === 'none' || currentPlanInterval === 'free') {
        this.setState({ planIntervalFilter: 'monthly' });
      } else {
        // If we have a montly or yearly plan set it to planIntervalFilter
        this.setState({ planIntervalFilter: currentPlanInterval });
      }
    }
  };

  updateDimensions = () => {
    this.setState({ width: window.innerWidth, height: window.innerHeight });
  };

  handleYearlyButton = node => {
    this.yearlyButton = node;
    this.forceUpdate();
  };

  clickedProductCard = (product, newPricingPlan) => {
    const { isEmbedded, userProfile, t } = this.props;
    const { coupon } = this.state;
    console.log('clickedProductCard', product, newPricingPlan);
    // if user clicked currentProduct
    if (!userProfile) {
      // not logged in. send the user to the sign-up page and then back here
      const path = `/signup?redirect=${encodeURIComponent('/products')}`;
      if (isEmbedded) {
        // the app is embedded. open the link in a new window using window.open()
        window.open(`${process.env.REACT_APP_WEB_HOST}${path}`, '_blank');
      } else {
        // the app is not embedded. use the react changePage to keep browsing history intact
        this.props.changePage(path);
      }
      return;
    }
    const { hostAppProxy } = this.props;
    if (hostAppProxy.settings.isWrapped) {
      if (hostAppProxy.settings.purchaseMode !== 'ios_iap') {
        toast.error(
          t('PurchasesUnavailable', 'Purchases are temporarily unavailable.')
        );
        return;
      }
      if (this.state.nativePurchaseProductId) {
        return;
      }
      const currentSubscription = userProfile.account.subscription;
      if (
        currentSubscription?.pricingPlan?.internalId ===
        newPricingPlan.internalId
      ) {
        return;
      }
      if (
        currentSubscription?.provider === 'PADDLE' &&
        currentSubscription?.pricingPlan?.internalId !== 'free'
      ) {
        toast.info(
          t(
            'ManageWebSubscription',
            'Your existing web subscription remains active. Manage it on betareader.io.'
          )
        );
        return;
      }
      if (newPricingPlan.externalId === 'free') {
        hostAppProxy.manageSubscriptions();
        return;
      }
      const appleProductId = appleProductIdForPlan(newPricingPlan.internalId);
      if (!appleProductId) {
        toast.error(
          t('PurchasesUnavailable', 'Purchases are temporarily unavailable.')
        );
        return;
      }
      this.setState({ nativePurchaseProductId: appleProductId }, () => {
        hostAppProxy.purchaseProduct(appleProductId);
      });
      return;
    }
    if (this.state.paddlePurchasePlanId) {
      return;
    }
    if (
      userProfile.account.subscription.pricingPlan.externalId ===
      newPricingPlan.externalId
    ) {
      return;
    }
    if (
      userProfile.account.subscription.provider === 'APPLE' &&
      userProfile.account.subscription.pricingPlan?.product?.internalId !==
        'free'
    ) {
      window.open(
        'https://apps.apple.com/account/subscriptions',
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }
    // track page view
    uxAnalyticsUtil.trackEvent({
      category: 'Subscriptions',
      action: 'clicked-subscription-plan',
      label: newPricingPlan.internalId,
    });

    // if user clicked on free when on a paid plan
    if (
      userProfile.account.subscription.pricingPlan.externalId !==
        newPricingPlan.externalId &&
      newPricingPlan.externalId === 'free'
    ) {
      this.showCancelModal(true);
      return;
    }

    // if user wanted to move from one payed plan to another payed plan
    if (
      userProfile.account.subscription.pricingPlan.externalId !==
        newPricingPlan.externalId &&
      newPricingPlan.externalId !== 'free' &&
      userProfile.account.subscription.pricingPlan.internalId !== 'free'
    ) {
      this.setState({
        newProductId: newPricingPlan.product,
        newPricingPlanId: newPricingPlan.externalId,
      });
      this.showChangePlanModal(true);
      return;
    }
    // else if user is on free and want to buy a product
    this.setState(
      { paddlePurchasePlanId: newPricingPlan.externalId },
      () => {
        try {
          window.Paddle.Checkout.open({
            coupon,
            product: newPricingPlan.externalId,
            email: userProfile.email,
            passthrough: JSON.stringify({
              account: userProfile.account._id,
              pricingPlan: newPricingPlan.externalId,
            }),
            title: `${t(product.title)} ${t(newPricingPlan.billingInterval)}`,
            message: t(product.description),
            loadCallback: data => this.onCheckoutLoad(data, newPricingPlan),
            successCallback: this.onCheckoutSuccess,
            closeCallback: this.onCheckoutClose,
          });
        } catch (e) {
          this.setState({ paddlePurchasePlanId: null });
          toast.error(t('SomethingWentWrong'));
        }
      }
    );
  };

  onCheckoutLoad = (checkoutData, pricingPlan) => {
    uxAnalyticsUtil.trackPageView(
      `${this.props.match.path}/${pricingPlan.internalId}`
    );
  };

  onCheckoutSuccess = async data => {
    this.setState({ paddlePurchasePlanId: null });
    const idToken = await authUtil.getFreshIdToken();
    this.showPaymentSuccessModal(true, data);
    this.props.getUserProfile(idToken);
  };

  onCheckoutClose = data => {
    const { t } = this.props;
    this.setState({ paddlePurchasePlanId: null });
    if (data && data.checkout && data.checkout.completed) {
      this.showPaymentSuccessModal(true, data);
    } else {
      toast.info(t('Cancelled'));
    }
  };

  showChangePlanModal = show => {
    this.setState({
      showChangePlanModal: show,
    });
  };

  showPaymentSuccessModal = (show, data) => {
    this.setState({
      showPaymentSuccessModal: show,
      paymentSuccessData: data,
    });
  };

  showCancelModal = show => {
    this.setState({
      showCancelModal: show,
    });
  };

  renderProducts() {
    const { products, userProfile } = this.props;
    const hostSettings = this.props.hostAppProxy?.settings || {};
    const {
      planIntervalFilter,
      nativePurchaseProductId,
      paddlePurchasePlanId,
    } = this.state;
    const purchasePendingProductId =
      nativePurchaseProductId || paddlePurchasePlanId;
    const usersSubscription =
      (userProfile &&
        userProfile.account &&
        userProfile.account.subscription) ||
      {};
    const usersPricingPlan = usersSubscription.pricingPlan || {};
    const usersProduct = usersPricingPlan.product || {};
    if (products.length > 0) {
      const productList = products.map(product => {
        const isCurrentProduct = usersProduct._id === product._id;
        const isCancelled =
          isCurrentProduct && usersSubscription.status === 'deleted';
        const validUntilDate = isCancelled && usersSubscription.validUntilDate;
        return (
          <ProductCard
            key={product._id}
            currentProduct={isCurrentProduct}
            isCancelled={isCancelled}
            validUntilDate={validUntilDate}
            currentPricingplanId={usersPricingPlan._id}
            currentBillingInterval={usersPricingPlan.billingInterval}
            planIntervalFilter={planIntervalFilter}
            product={product}
            pricingPlans={product.pricingPlans}
            onPress={(pricingPlan, localPriceObject) =>
              this.clickedProductCard(product, pricingPlan)
            }
            isLoggedIn={!!userProfile}
            nativeMode={Boolean(hostSettings.isWrapped)}
            nativeProducts={hostSettings.nativeProducts || []}
            purchasePendingProductId={purchasePendingProductId}
            purchaseInProgress={Boolean(purchasePendingProductId)}
          />
        );
      });
      return (
        <div className="subscription-products-cards">
          {productList}
        </div>
      );
    }
    return <Card>No products</Card>;
  }

  render() {
    const { isEmbedded, products, t } = this.props;
    const {
      showChangePlanModal,
      newPricingPlanId,
      newProductId,
      showCancelModal,
      planIntervalFilter,
      showPaymentSuccessModal,
      paymentSuccessData,
      nativePurchaseProductId,
      paddlePurchasePlanId,
    } = this.state;
    const hostSettings = this.props.hostAppProxy?.settings || {};
    const purchaseInProgress = Boolean(
      nativePurchaseProductId || paddlePurchasePlanId
    );
    if (products.length > 0) {
      return (
        <main className="subscription-products-grid">
          <div className="subscription-products-row">
            <h2>{t('Subscriptions')}</h2>
          </div>
          <div className="subscription-products-row">
            <div className="subscription-interval-switch" role="group" aria-label={t('BillingInterval', 'Billing interval')}>
              <button
                type="button"
                aria-pressed={planIntervalFilter === 'monthly'}
                disabled={purchaseInProgress}
                onClick={() => this.setState({ planIntervalFilter: 'monthly' })}
                className="subscription-interval-switch__button"
              >{t('Monthly')}</button>
              <button
                type="button"
                aria-pressed={planIntervalFilter === 'yearly'}
                disabled={purchaseInProgress}
                onClick={() => this.setState({ planIntervalFilter: 'yearly' })}
                className="subscription-interval-switch__button"
              >
                  <span className="subscription-interval-switch__yearly">
                    {t('Yearly')}
                    <small>
                      ({t('2MonthsFree')}!)
                    </small>
                  </span>
              </button>
            </div>
          </div>
          <div className="subscription-products-row">{this.renderProducts()}</div>
          {hostSettings.isWrapped && (
            <div className="subscription-products-row">
              {hostSettings.purchaseMode === 'ios_iap' ? (
                <div className="native-subscription-actions">
                  <Button
                    variant="secondary"
                    className="native-restore-purchases"
                    disabled={purchaseInProgress}
                    onClick={() =>
                      this.props.hostAppProxy.restorePurchases()
                    }
                  >{t('RestorePurchases', 'Restore purchases')}</Button>
                  <Button
                    variant="secondary"
                    className="native-manage-subscriptions"
                    disabled={purchaseInProgress}
                    onClick={() =>
                      this.props.hostAppProxy.manageSubscriptions()
                    }
                  >{t('ManageSubscription', 'Manage subscription')}</Button>
                </div>
              ) : (
                <span>
                  {t(
                    'PurchasesUnavailable',
                    'Purchases are temporarily unavailable.'
                  )}
                </span>
              )}
            </div>
          )}
          {hostSettings.purchaseMode === 'ios_iap' && (
            <div className="subscription-products-row">
              <p className="apple-subscription-renewal-terms">
                {t(
                  'AppleSubscriptionRenewalTerms',
                  'Payment is charged to your Apple Account. Subscriptions renew automatically unless cancelled at least 24 hours before the end of the current period. You can manage or cancel them in your App Store account settings.'
                )}
              </p>
            </div>
          )}
          <div className="subscription-products-row">
            <Media at="computer">
              {(className, renderChildren) =>
                renderChildren && <ProductToSMessage centered />
              }
            </Media>
          </div>
          <div className="subscription-products-row footer">
            {
              // only show footer when in standalone mode
              !isEmbedded && <Footer />
            }
            <ChangeSubscriptionModal
              newProductId={newProductId}
              newPricingPlanId={newPricingPlanId}
              planIntervalFilter={planIntervalFilter}
              open={showChangePlanModal}
              onClose={() => this.showChangePlanModal(false)}
            />
            <CancelSubscriptionModal
              open={showCancelModal}
              onClose={() => this.showCancelModal(false)}
            />
            {showPaymentSuccessModal && (
              <PaymentSuccessModal
                paymentSuccessData={paymentSuccessData}
                open={showPaymentSuccessModal}
                onClose={() => this.showPaymentSuccessModal(false, null)}
              />
            )}
          </div>
        </main>
      );
    }
    return null;
  }
}

// redux stuff
const mapStateToProps = state => ({
  isEmbedded: state.app.isEmbedded,
  user: state.user.user,
  idToken: state.user.idToken,
  userProfile: state.user.userProfile,
  products: state.subscription.products,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      fetchProducts,
      getUserProfile,
      changePage: newPage => push(newPage),
    },
    dispatch
  );

export default withTranslation()(
  withHostAppProxy(
    withRouter(connect(mapStateToProps, mapDispatchToProps)(Products))
  )
);
